/* Ring product pages v2: one file for both kinds of ring page (data-mode on the root).
   - Ready to ship (sections/ring-pdp.liquid; review card #1114, 10/28/2026): the ring is what it is. Carat and size chips choose a variant,
     only the sizes in stock can be chosen, and the only extras are the gift box and the printed gift note (no custom form: Aden 10/28/2026).
   - Made to order (sections/ring-custom-pdp.liquid; Aden 10/28/2026): metal and color, accent stones, ring size, engraving, hidden birthstone,
     production speed and packaging. The price follows the choices; a combination beyond the first 250 variants is looked up through
     templates/product.variant-json.liquid (?view=variant-json&option_values=...) before Add to bag or Shop Pay can be used.
     The same page serves made-to-order earrings (section setting kind = earrings; review card #1214, 11/2/2026): no ring size, engraving,
     birthstone or size helper, so nothing holds the buttons back.
   Shared: Add to bag (opens the cart drawer through CartAddEvent), Shop Pay held back until the required choices are made, the mobile buy bar,
   gallery counter / video / zoom, the three-tab size helper, Ask MYNSU question buttons, share, lazy reviews and the date estimates.
   Facts on the page (days, prices, SKUs) come from the Liquid render and the JSON block; nothing here invents a number. */
import { CartAddEvent, CartErrorEvent } from '@theme/events';

const PX_KEY = 'rp-pxmm';
const num = (s) => parseFloat(String(s).replace(/[^0-9.]/g, ''));
const usLabel = (n) => 'US ' + (n % 1 ? n.toFixed(1) : String(n));
const cap = (s) => String(s).charAt(0).toUpperCase() + String(s).slice(1);
// Inside diameter in mm for a US ring size (the same line as the size chart: US 4 = 14.9, US 7 = 17.3, US 13 = 22.2).
const diameter = (n) => 11.63 + 0.8128 * n;

function init(root) {
  const $ = (s, el = root) => el.querySelector(s);
  const $$ = (s, el = root) => Array.from(el.querySelectorAll(s));
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const smooth = reduced ? 'auto' : 'smooth';
  let data;
  try {
    data = JSON.parse($('[data-rp-json]').textContent);
  } catch {
    return;
  }
  const custom = data.mode === 'custom';

  const routes = (window.Theme && window.Theme.routes) || {};
  const addUrl = routes.cart_add_url || '/cart/add.js';
  const cartUrl = (routes.cart_url || '/cart') + '.js';

  const form = $('form.rp-form');
  const idInput = $('[data-rp-id]');
  const sizeFs = $('[data-rp-sizefs]');
  const price = $('[data-rp-price]');
  const addBtn = $('[data-rp-add]');
  const addText = $('[data-rp-addtext]');
  const dyn = $('[data-rp-dyn]');
  const block = $('[data-rp-block]');
  const err = $('[data-rp-err]');
  const sku = $('[data-rp-sku]');
  function showSku(x) {
    if (!sku) return;
    sku.textContent = x || '';
    sku.parentElement.hidden = !x;
  }
  // The part of the SKU every listed variant shares, cut at a dash (10982-1CT-US5 ... -US9 -> 10982-1CT); shown until a size is picked
  function skuStem(list) {
    const xs = (list || []).map((x) => x.x).filter(Boolean);
    if (!xs.length) return '';
    let a = xs[0];
    for (const x of xs) while (a && !x.startsWith(a)) a = a.slice(0, -1);
    if (xs.every((x) => x === a)) return a;
    return a.slice(0, a.lastIndexOf('-') + 1).replace(/-$/, '');
  }
  const money = moneyFormatter(data.money, data.currency, data.locale);
  // MYNSU Rewards line (snippets/store-credit-earn.liquid): the store credit this ring earns back follows the ring price
  const earn = $('[data-sc-earn]');
  // Gift box (ready-to-ship page): the standard box is included; an upgrade adds its price to what the buttons show
  const boxFs = $('[data-rp-boxes]');
  const boxVal = $('[data-rp-boxval]');
  function boxPick() {
    const r = boxFs?.querySelector('input.rp-r:checked');
    if (!r || r.value === 'standard') return null;
    return { key: r.value, name: r.dataset.n, cents: Number(r.dataset.c) || 0, variant: r.dataset.v || '' };
  }
  const bar = $('[data-rp-bar]');
  const barSel = $('[data-rp-barsel]');
  const barPrice = $('[data-rp-barprice]');
  const barBtn = $('[data-rp-barbtn]');

  function showErr(t) {
    err.textContent = t;
    err.hidden = false;
  }
  const hideErr = () => (err.hidden = true);
  const sizeRadios = () => (sizeFs ? $$('input.rp-r', sizeFs) : []);
  const sizePicked = () => sizeFs?.querySelector('input.rp-r:checked') || null;

  function shake(fs, msg) {
    if (!fs) return;
    fs.classList.remove('is-need');
    void fs.offsetWidth;
    fs.classList.add('is-need');
    showErr(msg);
    fs.scrollIntoView({ block: 'center', behavior: smooth });
    fs.querySelector('input.rp-r:not(:disabled)')?.focus({ preventScroll: true });
  }
  const needSize = () => shake(sizeFs, 'Please choose your ring size first.');

  /* ---------- what the buttons show: each kind of page fills in `view` and calls paint() ----------
     need: 'size' | 'month' (a required choice is missing) | 'wait' (the variant for this choice is still loading) | null */
  let view = { price: price.textContent, cents: null, need: null, sold: false, bar: '' };
  function paint() {
    const s = view;
    price.textContent = s.price;
    const box = boxPick();
    const total = box && s.cents != null ? money(s.cents + box.cents) : s.price;
    addBtn.disabled = s.sold || s.need === 'wait';
    addText.textContent = s.sold ? 'Sold out' : `Add to bag — ${total}`;
    if (earn && s.cents != null) {
      const c = Math.round(s.cents * Number(earn.dataset.rate));
      $('[data-sc-amt]', earn).textContent = money(c);
      earn.hidden = c < 1;
    }
    if (boxVal) boxVal.textContent = box ? `${box.name}, +${money(box.cents)}` : 'Standard box, included';
    // Shop Pay buys the ring line only: with a gift box upgrade the customer uses Add to bag
    dyn.classList.toggle('is-box', !!box);
    block.hidden = !s.need;
    const blk = s.need === 'month' ? 'Choose the birth month first' : s.need === 'wait' ? 'Loading your choice' : 'Select a size first';
    block.textContent = blk;
    block.setAttribute('aria-label', blk);
    const pay = dyn.querySelector('.shopify-payment-button');
    if (pay) pay.toggleAttribute('inert', !!s.need);
    dyn.hidden = s.sold;
    if (barSel) barSel.textContent = s.bar;
    if (barPrice) barPrice.textContent = total;
    if (barBtn) {
      barBtn.textContent = s.need === 'size' ? 'Select a size' : s.need === 'month' ? 'Choose the month' : s.sold ? 'Sold out' : 'Add to bag';
      barBtn.disabled = s.sold || s.need === 'wait';
    }
    if (s.need !== 'size') sizeFs?.classList.remove('is-need');
  }

  const eng = custom ? customEngine() : stockEngine();

  /* ---------- ready to ship: option chips -> variant ---------- */
  function stockEngine() {
    const optFs = $$('[data-rp-opt]');
    const caratFs = optFs.find((f) => f.dataset.kind === 'carat') || null;
    let cur = null;
    const pos = (f) => Number(f.dataset.rpOpt);
    const picked = (f) => f.querySelector('input.rp-r:checked')?.value ?? null;
    const sizeOk = () => !sizeFs || sizePicked() != null;
    function state() {
      const s = [];
      for (const f of optFs) s[pos(f) - 1] = picked(f);
      return s;
    }
    // needAll: every position must equal the choice; otherwise an empty choice matches anything.
    const match = (s, needAll) => data.v.filter((v) => v.o.every((x, i) => (s[i] == null ? !needAll : x === s[i])));

    function refreshSizes() {
      if (!sizeFs) return;
      const s = state();
      const sp = pos(sizeFs) - 1;
      for (const r of sizeRadios()) {
        const t = s.slice();
        t[sp] = r.value;
        const v = match(t, true)[0];
        const ok = !!(v && v.a);
        r.disabled = !ok;
        r.toggleAttribute('data-off', !ok);
        if (!ok && r.checked) r.checked = false;
      }
      const hint = $('[data-rp-sizehint]');
      if (hint) hint.hidden = !sizeRadios().some((r) => r.disabled);
    }

    function render({ changed = false, url = false } = {}) {
      const s = state();
      const ok = sizeOk();
      let v = ok ? match(s, true)[0] : null;
      let cands = v ? [v] : [];
      if (!v) {
        const c = match(s, false);
        cands = c;
        v = c.find((x) => x.a) || c[0] || data.v[0];
      }
      cur = v;
      if (idInput.value !== String(v.id)) {
        idInput.value = v.id;
        if (changed) idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      const parts = [];
      for (const f of optFs) {
        const out = f.querySelector('[data-rp-val]');
        const c = picked(f);
        let t = c ?? '';
        if (f.dataset.kind === 'size') t = c ?? 'Select a size';
        else if (f.dataset.kind === 'carat' && c) t = c + data.dew;
        if (out) out.textContent = t;
        parts.push(t);
      }
      view = { price: v.p, cents: v.c ?? null, need: ok ? null : 'size', sold: ok && !v.a, bar: parts.join(' · ') };
      paint();
      showSku(ok ? v.x : skuStem(cands));
      if (ok && url) {
        const u = new URL(location.href);
        u.searchParams.set('variant', v.id);
        u.searchParams.delete('carat');
        u.searchParams.delete('size');
        history.replaceState(history.state, '', u.pathname + u.search + u.hash);
      }
    }

    form.addEventListener('change', (e) => {
      const r = e.target;
      if (!(r instanceof HTMLInputElement) || !r.matches('[data-rp-opt] input.rp-r')) return;
      if (r.closest('[data-rp-opt]').dataset.kind !== 'size') refreshSizes();
      hideErr();
      render({ changed: true, url: true });
    });

    return {
      render: () => render(),
      need: needSize,
      item: () => (cur && cur.a ? { id: cur.id, quantity: 1 } : null),
      shareQuery: () => (sizeOk() && cur ? `?variant=${cur.id}` : ''),
      start() {
        // A readable link (?carat=2ct&size=7, card #1114 Q9) opens that choice. The address then shows Shopify's own ?variant=,
        // which works without scripts too (the server renders the chosen variant) and is the URL the structured data lists.
        const q = new URLSearchParams(location.search);
        let fromLink = false;
        for (const [f, raw] of [[caratFs, q.get('carat')], [sizeFs, q.get('size')]]) {
          if (!f || !raw) continue;
          refreshSizes();
          const r = $$('input.rp-r', f).find((x) => num(x.value) === num(raw));
          if (r && !r.disabled) {
            r.checked = true;
            fromLink = true;
          }
        }
        refreshSizes();
        render({ url: fromLink });
        stockEta();
      },
    };
  }

  /* ---------- made to order: metal type + color, accent stones, add-ons -> variant ---------- */
  function customEngine() {
    const D = data;
    const fmt = moneyFormatter(D.money, D.currency, D.locale);
    const pick = (n) => form.querySelector(`input[name="${n}"]:checked`);
    const colorIn = $$('input[name="color"]', form);
    const accIn = $$('input[name="accent_stones"]', form);
    const bOn = $('[data-rp-bon]');
    const monthsFs = $('[data-rp-months]');
    const engr = $('[data-rp-engr]');
    const engrCount = $('[data-rp-engrcount]');
    const band = $('[data-rp-band]');
    const bandText = $('[data-rp-bandtext]');
    const sumText = $('[data-rp-sumtext]');
    const total = $('[data-rp-total]');
    // Earrings (kind = earrings) have no size chooser: the size is not a required choice there
    const sized = !!sizeFs;
    const out = (k) => $(`[data-rp-val="${k}"]`);
    const setOut = (k, t) => {
      const el = out(k);
      if (el) el.textContent = t;
    };
    const setProp = (k, v) => {
      const el = $(`[data-rp-prop="${k}"]`);
      if (!el) return;
      el.value = v;
      // Shopify keeps a blank property and Shop Pay reads the form as it is: an empty one stays disabled.
      el.disabled = !v;
    };
    const byTC = new Map(D.mat.map((m, i) => [`${m.t}|${m.c}`, i]));
    const known = new Map(D.v.map((r) => [`${r[0]}.${r[1]}.${r[2]}`, { id: r[3], price: r[4], x: r[5] || '' }]));
    let key = '';
    let pending = false;
    let failed = false;
    let seq = 0;
    let userChanged = false;
    let s = null;

    function select() {
      const t = pick('metal')?.dataset.t;
      for (const r of colorIn) {
        const ok = byTC.has(`${t}|${r.dataset.c}`);
        r.disabled = !ok;
        r.toggleAttribute('data-off', !ok);
      }
      let c = pick('color');
      if (!c || c.disabled) {
        c = colorIn.find((r) => !r.disabled) || null;
        if (c) c.checked = true;
      }
      const color = c?.dataset.c || 'white';
      const mi = byTC.get(`${t}|${color}`) ?? 0;
      const ai = D.acc.length ? Math.max(0, accIn.findIndex((r) => r.checked)) : -1;
      const b = !!bOn?.checked;
      const rush = pick('production')?.value === D.parts.r;
      const gift = pick('packaging')?.dataset.gift === '1';
      const parts = [];
      if (b) parts.push(D.parts.b);
      if (rush) parts.push(D.parts.r);
      if (gift) parts.push(D.parts.g);
      const xn = parts.length ? parts.join(' + ') : D.parts.none;
      const xi = D.add.length ? D.add.findIndex((x) => x.n === xn) : -1;
      return { mi, ai, xi, b, rush, gift, color, bad: D.add.length > 0 && xi < 0 };
    }
    const keyOf = (q) => `${q.mi}.${q.ai}.${q.xi}`;
    const priceOf = (q) => D.mat[q.mi].b + (q.ai >= 0 ? D.acc[q.ai].add : 0) + (q.xi >= 0 ? D.add[q.xi].add : 0);

    function setId(id) {
      if (idInput.value !== String(id)) {
        idInput.value = id;
        // Shopify's installments banner follows this input.
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (!userChanged) return;
      const u = new URL(location.href);
      u.searchParams.set('variant', id);
      history.replaceState(history.state, '', u.pathname + u.search + u.hash);
    }

    function resolve(q) {
      const k = keyOf(q);
      if (k === key && !failed) return;
      key = k;
      failed = false;
      if (q.bad) {
        pending = false;
        failed = true;
        return;
      }
      const hit = known.get(k);
      if (hit) {
        pending = false;
        setId(hit.id);
        return;
      }
      // Beyond the first 250 variants: Shopify picks the variant from the option value ids.
      pending = true;
      const my = ++seq;
      const ids = [];
      const want = [];
      [
        [D.pos[0], D.mat, q.mi],
        [D.pos[1], D.acc, q.ai],
        [D.pos[2], D.add, q.xi],
      ].forEach(([p, arr, i]) => {
        if (p < 0 || i < 0) return;
        ids[p] = arr[i].id;
        want[p] = arr[i].n;
      });
      // No Accept: application/json header: with it Shopify answers with the product JSON and ignores ?view=.
      fetch(`${D.url}?view=variant-json&option_values=${ids.join(',')}`, { credentials: 'same-origin' })
        .then((r) => {
          if (!r.ok) throw new Error(String(r.status));
          return r.json();
        })
        .then((j) => {
          if (!j || !j.id || JSON.stringify(j.options) !== JSON.stringify(want)) throw new Error('not this combination');
          known.set(k, { id: j.id, price: j.price, x: j.x || '' });
          if (my !== seq) return;
          pending = false;
          setId(j.id);
          refresh();
        })
        .catch(() => {
          if (my !== seq) return;
          pending = false;
          failed = true;
          refresh();
        });
    }

    function refresh() {
      s = select();
      resolve(s);
      const hit = known.get(keyOf(s));
      const cents = hit ? hit.price : priceOf(s);
      const ptxt = fmt(cents);
      const size = pick('ring_size')?.value || '';
      const m = pick('birthstone_month');
      const e = engr ? engr.value.trim() : '';

      setOut('metal', D.mat[s.mi].n);
      setOut('color', cap(s.color));
      if (s.ai >= 0) setOut('accent', D.acc[s.ai].n);
      setOut('size', size || 'Select a size');
      setOut('month', m ? `${m.value}, ${m.dataset.stone} color` : 'Choose the month');
      setOut('production', s.rush ? D.parts.r : 'Standard');
      setOut('packaging', pick('packaging')?.value || '');
      if (monthsFs) monthsFs.hidden = !s.b;

      if (engr && engrCount) engrCount.textContent = `${engr.value.length} / ${engr.maxLength}`;
      if (bandText) bandText.textContent = e || 'Preview';
      if (band) band.className = `rp-band rp-band--${s.color}${e ? '' : ' is-ph'}`;

      setProp('size', size);
      setProp('engraving', e);
      setProp('birthstone', s.b && m ? `${m.value} (${m.dataset.stone} color)` : '');

      const items = [D.mat[s.mi].n];
      if (s.ai >= 0) items.push(s.ai === 0 ? 'Accent stones as shown' : `${D.acc[s.ai].n} accent stones`);
      if (sized) items.push(size || 'Size not chosen');
      if (e) items.push(`Engraving: ${e}`);
      if (s.b) items.push(m ? `${D.parts.b}: ${m.value}` : `${D.parts.b}: month not chosen`);
      if (s.rush) items.push(D.parts.r);
      if (s.gift) items.push(pick('packaging')?.value || D.parts.g);
      if (sumText) {
        sumText.replaceChildren(
          ...items.map((t) => {
            const sp = document.createElement('span');
            sp.textContent = t;
            if (/not chosen$/.test(t)) sp.className = 'is-need';
            return sp;
          })
        );
      }
      if (total) total.textContent = ptxt;
      showSku(hit && !pending && !failed ? hit.x : '');

      const need = failed || pending ? 'wait' : sized && !size ? 'size' : s.b && !m ? 'month' : null;
      view = { price: ptxt, cents, need: need === 'wait' && sized && !size ? 'size' : need, sold: false, bar: sized ? `${D.mat[s.mi].n} · ${size || 'Select a size'}` : D.mat[s.mi].n };
      paint();
      if (failed) showErr('We could not load the price for this combination. Please choose it again or refresh the page.');
      customEta(s.rush);
    }

    form.addEventListener('change', (e) => {
      const t = e.target;
      if (!(t instanceof HTMLInputElement) || t.type === 'hidden') return;
      // An assistant may choose a month without ticking the box: tick it so the price and the summary show it.
      if (t.name === 'birthstone_month' && bOn && !bOn.checked) bOn.checked = true;
      userChanged = true;
      hideErr();
      if (t.name === 'ring_size') {
        const u = new URL(location.href);
        u.searchParams.set('size', String(num(t.value)));
        history.replaceState(history.state, '', u.pathname + u.search + u.hash);
      }
      refresh();
    });
    engr?.addEventListener('input', refresh);

    return {
      render: refresh,
      need() {
        if (sized && !sizePicked()) return needSize();
        if (s?.b && !pick('birthstone_month')) return shake(monthsFs, 'Please choose the birth month for the hidden birthstone.');
        if (failed) {
          key = '';
          refresh();
        }
      },
      item() {
        if (pending || failed) return null;
        const properties = {};
        for (const el of $$('[data-rp-prop]', form)) if (!el.disabled && el.value) properties[el.name.slice(11, -1)] = el.value;
        return { id: Number(idInput.value), quantity: 1, properties };
      },
      shareQuery() {
        const z = sizePicked();
        return `?variant=${idInput.value}${z ? `&size=${num(z.value)}` : ''}`;
      },
      start() {
        const raw = new URLSearchParams(location.search).get('size');
        if (raw) {
          const r = sizeRadios().find((x) => num(x.value) === num(raw));
          if (r) r.checked = true;
        }
        refresh();
      },
    };
  }

  /* ---------- gift (ready-to-ship page): the printed note (the gift box chooser is wired next to paint()) ---------- */
  const noteOn = $('[data-rp-noteon]');
  const noteBox = $('[data-rp-note]');
  const noteText = $('[data-rp-notetext]');
  const noteCount = $('[data-rp-notecount]');
  const noteProp = $('[data-rp-noteprop]');
  // The textarea (name "gift_note") is what people and assistants type into; Shopify ignores that name.
  // The order line property is the hidden properties[Gift note] input: enabled only while the box is ticked and the
  // note has text, because Shopify keeps a blank property and Shop Pay reads the form as it is.
  const syncNote = (focus) => {
    if (!noteOn || !noteText) return;
    const typed = noteText.value.trim();
    // An assistant may fill the note without ticking the box: tick it so the customer sees the note before buying.
    if (typed && !noteOn.checked && !noteOn.dataset.userOff) noteOn.checked = true;
    noteBox.hidden = !noteOn.checked;
    if (noteProp) {
      noteProp.value = noteOn.checked ? typed : '';
      noteProp.disabled = !noteProp.value;
    }
    if (noteCount) noteCount.textContent = `${noteText.value.length} / 200`;
    if (focus && noteOn.checked) noteText.focus();
  };
  if (noteOn && noteText) {
    noteOn.addEventListener('change', () => {
      if (noteOn.checked) delete noteOn.dataset.userOff;
      else noteOn.dataset.userOff = '1';
      syncNote(true);
    });
    noteText.addEventListener('input', () => {
      delete noteOn.dataset.userOff;
      syncNote(false);
    });
    noteText.addEventListener('change', () => syncNote(false));
    syncNote(false);
  }
  boxFs?.addEventListener('change', () => paint());

  /* ---------- Add to bag ---------- */
  let adding = false;
  async function add() {
    if (adding) return;
    if (view.need) return eng.need();
    const item = eng.item();
    if (!item) return;
    adding = true;
    addBtn.disabled = true;
    addBtn.setAttribute('aria-busy', 'true');
    if (barBtn) barBtn.disabled = true;
    hideErr();
    syncNote(false);
    const note = noteProp && !noteProp.disabled ? noteProp.value : '';
    if (note) item.properties = { ...(item.properties || {}), 'Gift note': note };
    const box = boxPick();
    if (box && !box.variant) item.properties = { ...(item.properties || {}), 'Gift box': box.name };
    if (item.properties && !Object.keys(item.properties).length) delete item.properties;
    const items = [item];
    if (box && box.variant) items.push({ id: Number(box.variant), quantity: 1 });
    const sections = $$('cart-items-component', document)
      .map((n) => n.dataset.sectionId)
      .filter(Boolean)
      .join(',');
    try {
      const res = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections }),
      });
      const j = await res.json();
      if (j.status) {
        showErr(j.description || j.message || 'Something went wrong. Please try again.');
        form.dispatchEvent(new CartErrorEvent(form.getAttribute('id') || '', j.message, j.description, j.errors));
        return;
      }
      let cart = null;
      try {
        cart = await (await fetch(cartUrl)).json();
      } catch {
        /* the drawer still updates from the sections */
      }
      form.dispatchEvent(
        new CartAddEvent(cart || {}, String(item.id), {
          source: 'ring-pdp',
          itemCount: items.length,
          productId: String(data.pid),
          variantId: String(item.id),
          sections: j.sections,
        })
      );
      addText.textContent = 'Added to bag';
      setTimeout(() => eng.render(), 2000);
    } catch {
      showErr('We could not add it to your bag. Please check your connection and try again.');
    } finally {
      adding = false;
      addBtn.removeAttribute('aria-busy');
      addBtn.disabled = view.sold || view.need === 'wait';
      if (barBtn) barBtn.disabled = view.sold || view.need === 'wait';
    }
  }
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    add();
  });
  // Shop Pay reads the form when it is pressed: settle the gift note property first.
  for (const t of ['pointerdown', 'keydown']) dyn.addEventListener(t, () => syncNote(false), true);
  block.addEventListener('click', () => eng.need());
  barBtn?.addEventListener('click', () => (view.need ? eng.need() : add()));

  /* ---------- buy bar (under 1000 px: always there unless the main button is on screen; card #1114 Q2)
     and the sticky panel (1000 px and up) ---------- */
  const narrow = matchMedia('(max-width: 999px)');
  const cta = $('[data-rp-cta]');
  let ctaSeen = false;
  const setStuck = () => (bar.dataset.stuck = String(narrow.matches && !ctaSeen));
  if (bar && cta && 'IntersectionObserver' in window) {
    new IntersectionObserver((entries) => {
      for (const en of entries) ctaSeen = en.isIntersecting;
      setStuck();
    }).observe(cta);
    narrow.addEventListener?.('change', setStuck);
  }
  // Keep the page's bottom padding equal to the bar's real height (a long material name wraps to two lines)
  if (bar && 'ResizeObserver' in window) {
    new ResizeObserver(() => document.documentElement.style.setProperty('--rp-barh', Math.ceil(bar.getBoundingClientRect().height) + 'px')).observe(bar);
  }
  const panel = $('[data-rp-panel]');
  const media = $('.rp-media');
  // Both columns are sticky on wide screens; only the shorter one moves (CSS). A column taller than the window
  // scrolls until its end shows, then holds (a negative top).
  function fitPanel() {
    const hh = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
    for (const [el, v] of [[panel, '--rp-top'], [media, '--rp-mtop']]) {
      if (!el) continue;
      if (narrow.matches) el.style.removeProperty(v);
      else el.style.setProperty(v, Math.min(hh + 16, innerHeight - el.offsetHeight - 16) + 'px');
    }
  }
  if (panel) {
    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(fitPanel);
      ro.observe(panel);
      if (media) ro.observe(media);
    }
    addEventListener('resize', fitPanel, { passive: true });
    fitPanel();
  }

  /* ---------- gallery: counter, video, zoom ---------- */
  const track = $('[data-rp-track]');
  const cnt = $('[data-rp-cnt]');
  const dots = $$('.rp-dots i');
  let raf = 0;
  track?.addEventListener(
    'scroll',
    () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        // The slide whose start is nearest the scroll position (slides are full width on phones, 72% on tablets); at the end, the last one.
        const kids = track.children;
        const base = kids[0]?.offsetLeft || 0;
        let i = 0;
        if (track.scrollLeft >= track.scrollWidth - track.clientWidth - 2) i = kids.length - 1;
        else {
          let best = Infinity;
          for (let k = 0; k < kids.length; k++) {
            const dist = Math.abs(kids[k].offsetLeft - base - track.scrollLeft);
            if (dist < best) {
              best = dist;
              i = k;
            }
          }
        }
        if (cnt) cnt.textContent = String(i + 1);
        dots.forEach((d, k) => d.classList.toggle('on', k === i));
      });
    },
    { passive: true }
  );
  const vf = $('[data-rp-video]');
  vf?.querySelector('[data-rp-play]')?.addEventListener('click', () => {
    if (vf.querySelector('video')) return;
    const v = document.createElement('video');
    v.src = vf.dataset.rpVideo;
    v.controls = true;
    v.playsInline = true;
    v.preload = 'auto';
    v.poster = vf.querySelector('img')?.currentSrc || '';
    v.setAttribute('aria-label', `Video of the ${data.title}`);
    vf.appendChild(v);
    vf.classList.add('is-playing');
    v.play().catch(() => {});
    v.focus();
  });

  const zd = $('[data-rp-zoomd]');
  const zt = $('[data-rp-ztrack]');
  let zBuilt = false;
  function buildZoom() {
    if (zBuilt) return;
    zBuilt = true;
    for (const m of data.img) {
      const cell = document.createElement('div');
      if (m.v) {
        const v = document.createElement('video');
        v.src = m.v;
        v.poster = m.s;
        v.controls = true;
        v.playsInline = true;
        v.preload = 'none';
        v.setAttribute('aria-label', `Video of the ${data.title}`);
        cell.appendChild(v);
      } else {
        const im = new Image();
        im.src = m.s;
        im.alt = m.a || '';
        im.decoding = 'async';
        im.loading = 'lazy';
        cell.appendChild(im);
      }
      zt.appendChild(cell);
    }
  }
  const lock = (on) => (document.documentElement.style.overflow = on ? 'hidden' : '');
  $$('[data-rp-zoom]').forEach((b) =>
    b.addEventListener('click', () => {
      buildZoom();
      zd.showModal();
      lock(true);
      const i = Number(b.dataset.rpZoom) || 0;
      requestAnimationFrame(() => zt.scrollTo({ left: i * zt.clientWidth, behavior: 'instant' }));
    })
  );
  const zstep = (d) => zt.scrollBy({ left: d * zt.clientWidth, behavior: smooth });
  $('[data-rp-zprev]')?.addEventListener('click', () => zstep(-1));
  $('[data-rp-znext]')?.addEventListener('click', () => zstep(1));
  zd?.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowLeft') zstep(-1);
    else if (e.key === 'ArrowRight') zstep(1);
  });
  zd?.addEventListener('close', () => {
    lock(false);
    zt.querySelectorAll('video').forEach((v) => v.pause());
  });
  $$('[data-rp-close]').forEach((b) => b.addEventListener('click', () => b.closest('dialog')?.close()));

  /* ---------- size helper ---------- */
  // The earrings page has no size helper: a detached dialog keeps the lookups below harmless
  const szd = $('[data-rp-szd]') || document.createElement('dialog');
  const calBox = $('[data-rp-cal]', szd);
  const cmpBox = $('[data-rp-cmpw]', szd);
  const calR = $('[data-rp-calr]', szd);
  const szOut = $('[data-rp-szout]', szd);
  const szMm = $('[data-rp-szmm]', szd);
  const szPick = $('[data-rp-szpick]', szd);
  const szMsg = $('[data-rp-szmsg]', szd);
  let szVal = 7;
  const sizeRadio = (n) => sizeRadios().find((r) => num(r.value) === n) || null;
  const sizeAvail = (n) => {
    const r = sizeRadio(n);
    return !!(r && !r.disabled);
  };
  function notAvail(n) {
    const list = sizeRadios()
      .filter((r) => !r.disabled)
      .map((r) => num(r.value));
    if (!list.length) return 'This ring is sold out in every size.';
    return `${usLabel(n)} is not available for this ring. In stock: ${list.map(usLabel).join(', ')}.`;
  }
  function savedPx() {
    try {
      const v = parseFloat(localStorage.getItem(PX_KEY));
      return v >= 2 && v <= 9 ? v : null;
    } catch {
      return null;
    }
  }
  // A first guess before the card check: phones run about 5.5 CSS px per mm, computers about 3.8.
  const guessPx = () => (matchMedia('(pointer: coarse)').matches && Math.min(screen.width, screen.height) < 500 ? 5.6 : 3.78);
  const setPx = (v) => szd.style.setProperty('--pxmm', String(v));
  function showCal(on) {
    calBox.hidden = !on;
    cmpBox.hidden = on;
  }
  function renderSz() {
    const d = diameter(szVal);
    szd.style.setProperty('--dmm', d.toFixed(2));
    szOut.textContent = usLabel(szVal);
    szMm.textContent = `Inside diameter about ${d.toFixed(1)} mm`;
    const ok = sizeAvail(szVal);
    szPick.disabled = !ok;
    szPick.textContent = ok ? `Choose ${usLabel(szVal)}` : `${usLabel(szVal)} is not available`;
    szMsg.textContent = ok ? '' : notAvail(szVal);
  }
  function choose(n) {
    szVal = n;
    const r = sizeRadio(n);
    if (r && !r.disabled) {
      r.checked = true;
      r.dispatchEvent(new Event('change', { bubbles: true }));
      szd.close();
      r.focus({ preventScroll: true });
      return;
    }
    renderSz();
  }
  function openSize() {
    const was = sizePicked();
    szVal = was ? num(was.value) : 7;
    const px = savedPx();
    setPx(px || guessPx());
    calR.value = String(px || guessPx());
    showCal(!px);
    tab(0);
    renderSz();
    szd.showModal();
  }
  $$('[data-rp-sizehelp]').forEach((b) => b.addEventListener('click', openSize));
  calR?.addEventListener('input', () => setPx(calR.value));
  $('[data-rp-caldone]', szd)?.addEventListener('click', () => {
    try {
      localStorage.setItem(PX_KEY, calR.value);
    } catch {
      /* private mode: keep it for this visit */
    }
    showCal(false);
    renderSz();
  });
  $('[data-rp-recal]', szd)?.addEventListener('click', () => showCal(true));
  $('[data-rp-szdown]', szd)?.addEventListener('click', () => {
    szVal = Math.max(3, szVal - 0.5);
    renderSz();
  });
  $('[data-rp-szup]', szd)?.addEventListener('click', () => {
    szVal = Math.min(13, szVal + 0.5);
    renderSz();
  });
  szPick?.addEventListener('click', () => choose(szVal));
  const tabs = $$('[data-rp-tab]', szd);
  const tps = $$('[data-rp-tp]', szd);
  function tab(i, focus) {
    tabs.forEach((t, k) => {
      t.setAttribute('aria-selected', String(k === i));
      t.tabIndex = k === i ? 0 : -1;
    });
    tps.forEach((p, k) => (p.hidden = k !== i));
    if (focus) tabs[i].focus();
  }
  tabs.forEach((t, i) => t.addEventListener('click', () => tab(i)));
  $('[role=tablist]', szd)?.addEventListener('keydown', (e) => {
    const i = tabs.indexOf(document.activeElement);
    if (i < 0) return;
    const n = { ArrowRight: i + 1, ArrowLeft: i - 1, Home: 0, End: tabs.length - 1 }[e.key];
    if (n == null) return;
    e.preventDefault();
    tab((n + tabs.length) % tabs.length, true);
  });
  // Size chart rows: the US cell becomes a button; a click anywhere on the row chooses that size.
  for (const tr of $$('[data-rp-chart] tbody tr', szd)) {
    const td = tr.cells[0];
    const n = num(td?.textContent);
    if (!td || !Number.isFinite(n)) continue;
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'rp-szbtn';
    b.textContent = td.textContent.trim();
    b.setAttribute('aria-label', `Choose ${usLabel(n)}`);
    td.textContent = '';
    td.appendChild(b);
    tr.addEventListener('click', () => choose(n));
  }
  $('[data-rp-print]', szd)?.addEventListener('click', printSizer);
  for (const dlg of [szd, zd]) {
    dlg?.addEventListener('click', (e) => {
      if (e.target !== dlg) return;
      const r = dlg.getBoundingClientRect();
      if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) dlg.close();
    });
  }

  /* ---------- Ask MYNSU question buttons ---------- */
  // q = a question to send at once; chips = this page's starter questions shown in the window (no question sent).
  function askMynsu(q, chips) {
    const go = () => {
      const A = window.AskMYNSU;
      if (!A) return false;
      if (q && A.ask) A.ask(q);
      else if (chips && chips.length && A.openWith) A.openWith(chips);
      else A.open();
      return true;
    };
    if (go()) return;
    // The launcher fetches its script on pointerenter; reuse that loader instead of a second copy.
    document.querySelector('#askm .askm-b')?.dispatchEvent(new Event('pointerenter'));
    let n = 0;
    const t = setInterval(() => {
      if (go() || ++n > 80) clearInterval(t);
    }, 100);
  }
  $('[data-rp-ask]')?.addEventListener('click', (e) => {
    let chips = [];
    try {
      chips = JSON.parse(e.currentTarget.dataset.chips || '[]');
    } catch (err) {
      chips = [];
    }
    askMynsu('', chips);
  });

  /* One Ask entry at a time: while the buy panel (it holds the Ask capsule) is on screen the corner launcher steps aside,
     so it never covers the price, the options or the buy buttons; it comes back below the panel. */
  const askPanel = $('[data-rp-ask]') ? $('[data-rp-panel]') : null;
  if (askPanel && 'IntersectionObserver' in window) {
    new IntersectionObserver((es) => {
      for (const e of es) document.documentElement.toggleAttribute('data-rp-askcap', e.isIntersecting);
    }).observe(askPanel);
  }

  /* ---------- share ---------- */
  $('[data-rp-share]')?.addEventListener('click', async (e) => {
    const b = e.currentTarget;
    const url = location.origin + data.url + eng.shareQuery();
    if (navigator.share) {
      try {
        await navigator.share({ title: data.title, url });
      } catch {
        /* closed */
      }
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      b.setAttribute('data-done', '');
      setTimeout(() => b.removeAttribute('data-done'), 2000);
    } catch {
      /* no clipboard */
    }
  });

  /* ---------- reviews: Judge.me loads only when the row opens (no reviews yet), as in blocks/_accordion-row.liquid ---------- */
  const rv = $('#rp-reviews');
  $('.rp-stars')?.addEventListener('click', () => rv && (rv.open = true));
  const tpl = rv?.querySelector('template[data-rp-lazy-reviews]');
  if (tpl) {
    let done = false;
    const load = () => {
      if (done) return;
      done = true;
      tpl.replaceWith(tpl.content.cloneNode(true));
      const jm = window.jdgm;
      if (!jm || !jm._loaderExecuted || jm._blocked) return;
      const loader = document.querySelector('script[src*="judgeme"][src$="/loader.js"]');
      if (!loader) return;
      jm._loaderExecuted = false;
      const s = document.createElement('script');
      s.src = loader.src;
      s.async = true;
      document.body.appendChild(s);
    };
    if (/#judgeme/.test(location.hash) || /[?&]judgeme_[a-z_]+=/.test(location.search)) {
      rv.open = true;
      load();
      requestAnimationFrame(() => (document.getElementById('judgeme_product_reviews') || rv).scrollIntoView({ block: 'start' }));
    } else {
      rv.addEventListener('toggle', () => rv.open && load());
    }
  }

  /* ---------- date estimates ---------- */
  const eta = $('[data-rp-eta]');
  const d = root.dataset;
  const int = (x) => (x === '' || x == null ? NaN : parseInt(x, 10));
  // Ready to ship: U.S. arrival from the handling and shipping business days on the page.
  function stockEta() {
    const days = [d.hmin, d.hmax, d.smin, d.smax].map(int);
    if (!eta || d.country !== 'US' || !days.every(Number.isFinite)) return;
    const today = new Date();
    const a = addBusinessDays(today, days[0] + days[2]);
    const b = addBusinessDays(today, days[1] + days[3]);
    const ret = (eta.textContent.match(/(\d+-day returns)/) || [])[1];
    eta.textContent = `Estimated arrival ${dateRange(a, b)} · free U.S. shipping${ret ? ` · ${ret}` : ''}`;
  }
  // Made to order: the ship date from the production days (calendar days after payment); Rush moves both ends earlier.
  const freeShip = !!eta && /free U\.S\. shipping/.test(eta.textContent);
  function customEta(rush) {
    let [a, b] = [int(d.pmin), int(d.pmax)];
    const [rmin, rmax] = [int(d.rmin), int(d.rmax)];
    if (!eta || !Number.isFinite(a) || !Number.isFinite(b)) return;
    if (rush && Number.isFinite(rmin) && Number.isFinite(rmax)) [a, b] = [a - rmax, b - rmin];
    const today = new Date();
    eta.textContent = `Estimated ship date ${dateRange(addDays(today, a), addDays(today, b))}${freeShip ? ' · free U.S. shipping' : ''}`;
  }

  eng.start();
}

/* ---------- money in the store's own format: the Liquid money filter on 1,234,567.89 is the pattern ---------- */
function moneyFormatter(sample, currency, locale) {
  const t = document.createElement('textarea');
  t.innerHTML = String(sample || '');
  const m = t.value.match(/^([\s\S]*?)1(\D?)234\2567(?:(\D)(\d{2}))?([\s\S]*)$/);
  if (m) {
    const [, pre, th, dec, cents, post] = m;
    return (c) => {
      const v = Math.round(Math.abs(c));
      const whole = String(Math.floor(v / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, th);
      return (c < 0 ? '-' : '') + pre + whole + (cents ? dec + String(v % 100).padStart(2, '0') : '') + post;
    };
  }
  try {
    const nf = new Intl.NumberFormat(locale || 'en', { style: 'currency', currency: currency || 'USD' });
    return (c) => nf.format(c / 100);
  } catch {
    return (c) => (c / 100).toFixed(2);
  }
}

/* ---------- dates (U.S. federal holidays, observed, for business days) ---------- */
function nthWeekday(y, m, wd, n) {
  const first = new Date(y, m, 1).getDay();
  return new Date(y, m, 1 + ((wd - first + 7) % 7) + 7 * (n - 1));
}
function lastWeekday(y, m, wd) {
  const last = new Date(y, m + 1, 0);
  return new Date(y, m, last.getDate() - ((last.getDay() - wd + 7) % 7));
}
function observed(y, m, day) {
  const dt = new Date(y, m, day);
  if (dt.getDay() === 6) dt.setDate(day - 1);
  else if (dt.getDay() === 0) dt.setDate(day + 1);
  return dt;
}
const holidayCache = new Map();
function holidays(y) {
  if (!holidayCache.has(y)) {
    const list = [
      observed(y, 0, 1),
      nthWeekday(y, 0, 1, 3),
      nthWeekday(y, 1, 1, 3),
      lastWeekday(y, 4, 1),
      observed(y, 5, 19),
      observed(y, 6, 4),
      nthWeekday(y, 8, 1, 1),
      nthWeekday(y, 9, 1, 2),
      observed(y, 10, 11),
      nthWeekday(y, 10, 4, 4),
      observed(y, 11, 25),
      observed(y + 1, 0, 1),
    ];
    holidayCache.set(y, new Set(list.map((x) => x.toDateString())));
  }
  return holidayCache.get(y);
}
function addBusinessDays(from, n) {
  const dt = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  let left = n;
  while (left > 0) {
    dt.setDate(dt.getDate() + 1);
    const wd = dt.getDay();
    if (wd === 0 || wd === 6 || holidays(dt.getFullYear()).has(dt.toDateString())) continue;
    left -= 1;
  }
  return dt;
}
function addDays(from, n) {
  return new Date(from.getFullYear(), from.getMonth(), from.getDate() + n);
}
function dateRange(a, b) {
  const md = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
  if (a.getMonth() === b.getMonth()) return `${md.format(a)} – ${b.getDate()}`;
  return `${md.format(a)} – ${md.format(b)}`;
}

/* ---------- printable sizer: one page, true size in millimeters ---------- */
function printSizer() {
  const html = sizerHtml();
  const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
  const w = window.open(url, '_blank');
  if (w) return;
  // Pop-ups blocked: print from a hidden frame instead.
  const f = document.createElement('iframe');
  f.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0';
  f.srcdoc = html;
  f.addEventListener('load', () => {
    try {
      f.contentWindow.print();
    } catch {
      /* nothing else to try */
    }
  });
  document.body.appendChild(f);
}
function sizerHtml() {
  const t = (x, y, s, txt, extra = '') => `<text x="${x}" y="${y}" font-size="${s}" ${extra}>${txt}</text>`;
  let svg = '';
  // 100 mm check line
  svg += t(10, 14, 6, 'MYNSU ring sizer', 'font-weight="600"');
  svg += t(10, 21, 3.4, 'Print at 100% (actual size). The line below must measure exactly 100 mm with a ruler.');
  svg += '<line x1="10" y1="28" x2="110" y2="28" stroke="#14171c" stroke-width="0.4"/>';
  for (let i = 0; i <= 10; i++) svg += `<line x1="${10 + i * 10}" y1="26" x2="${10 + i * 10}" y2="30" stroke="#14171c" stroke-width="0.3"/>`;
  svg += t(112, 29.2, 3, '100 mm');
  // Paper strip: cut out, wrap round the finger, read the size where the zero end meets the scale.
  // Whole sizes are 2.55 mm apart, so odd and even labels sit on two rows.
  const y0 = 44;
  svg += t(10, y0 - 4, 4, 'Paper sizer strip', 'font-weight="600"');
  svg += `<rect x="10" y="${y0}" width="82" height="16" fill="none" stroke="#14171c" stroke-width="0.3" stroke-dasharray="1.5 1"/>`;
  svg += `<line x1="12" y1="${y0}" x2="12" y2="${y0 + 16}" stroke="#14171c" stroke-width="0.5"/>`;
  svg += t(13.2, y0 + 4.2, 2.4, '0');
  svg += t(13.2, y0 + 13.8, 2.2, 'US size');
  for (let s = 3; s <= 13; s += 0.5) {
    const x = 12 + Math.PI * diameter(s);
    const whole = s % 1 === 0;
    svg += `<line x1="${x.toFixed(2)}" y1="${y0 + 16}" x2="${x.toFixed(2)}" y2="${y0 + (whole ? 9 : 12)}" stroke="#14171c" stroke-width="0.25"/>`;
    if (whole) svg += t(x.toFixed(2), y0 + (s % 2 ? 4.2 : 7.6), 2.4, String(s), 'text-anchor="middle"');
  }
  svg += t(10, y0 + 22, 3.2, 'Cut along the dashed line. Wrap the strip round the base of your finger, numbers outward,');
  svg += t(10, y0 + 26.5, 3.2, 'and read the US size where the zero end meets the scale. Between two sizes, choose the larger.');
  // Circles: place a ring you own on them; the right size lines up with the inside edge of the ring.
  const c0 = 92;
  svg += t(10, c0, 4, 'Ring circles (inside diameter)', 'font-weight="600"');
  svg += t(10, c0 + 5, 3.2, 'Put a ring you already wear on the circles. Your size is the circle that lines up with the inside edge.');
  let x = 10;
  let y = c0 + 14;
  for (let s = 4; s <= 13; s++) {
    const dmm = diameter(s);
    if (x + dmm > 185) {
      x = 10;
      y += 34;
    }
    svg += `<circle cx="${(x + dmm / 2).toFixed(2)}" cy="${(y + 12).toFixed(2)}" r="${(dmm / 2).toFixed(2)}" fill="none" stroke="#14171c" stroke-width="0.3"/>`;
    svg += t((x + dmm / 2).toFixed(2), (y + 28.5).toFixed(2), 3, `US ${s}`, 'text-anchor="middle"');
    x += dmm + 8;
  }
  return `<!doctype html><html lang="en"><head><meta charset="utf-8"><title>MYNSU ring sizer</title>
<style>@page{size:auto;margin:10mm}html,body{margin:0;font-family:Inter,Helvetica,Arial,sans-serif;color:#14171c}svg{display:block}</style></head>
<body><svg xmlns="http://www.w3.org/2000/svg" width="190mm" height="190mm" viewBox="0 0 190 190" fill="#14171c">${svg}</svg>
<script>addEventListener('load',function(){setTimeout(function(){print()},200)})<\/script></body></html>`;
}

/* ---------- start (last, so every constant above is initialized) ---------- */
function boot(root) {
  if (!root || root.hasAttribute('data-rp-ready')) return;
  root.setAttribute('data-rp-ready', '');
  init(root);
}
boot(document.querySelector('[data-rp]'));
document.addEventListener('shopify:section:load', (e) => boot(e.target?.querySelector?.('[data-rp]')));
